// src/components/TopNav.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Search, Bell, Settings, LogOut, Plus, ChevronDown, 
  Users, FileText, Building, Calculator, Mail,
  CreditCard, User, Shield, HelpCircle, Home, ChevronRight
} from 'lucide-react';

const pathMap = {
  dashboard: 'Dashboard',
  applicants: 'Bewerber',
  'applicants-add': 'Neuer Bewerber',
  'applicant-details': 'Bewerberdetails',
  partners: 'Partner',
  'partners-add': 'Neuer Partner',
  'partner-details': 'Partnerdetails',
  statements: 'Abrechnungen',
  'statements-add': 'Neue Abrechnung',
  settings: 'Einstellungen',
  employees: 'Mitarbeiter',
  customers: 'Kunden',
  locations: 'Standorte',
  'locations-add': 'Neue Location',
  invoices: 'Rechnungen',
  payments: 'Zahlungen',
  profile: 'Profil',
  account: 'Account',
  security: 'Sicherheit',
  docs: 'Dokumentation',
  support: 'Support',
  help: 'Hilfe',
  search: 'Suche'
};

const quickActions = [
  {
    title: "Bewerber & Personal",
    items: [
      { name: 'Neuer Bewerber', path: '/applicants/add', icon: User, permission: 'create:applicants' },
      { name: 'Neue Stelle', path: '/jobs/add', icon: FileText, permission: 'create:jobs' },
      { name: 'Neuer Mitarbeiter', path: '/employees/add', icon: Users, permission: 'create:employees' }
    ]
  },
  {
    title: "Partner & Kunden",
    items: [
      { name: 'Neuer Partner', path: '/partners/add', icon: Building, permission: 'create:partners' },
      { name: 'Neuer Kunde', path: '/customers/add', icon: Users, permission: 'create:customers' },
      { name: 'Neue Location', path: '/locations/add', icon: Building, permission: 'create:locations' }
    ]
  },
  {
    title: "Finanzen",
    items: [
      { name: 'Neue Abrechnung', path: '/statements/add', icon: Calculator, permission: 'create:statements' },
      { name: 'Neue Rechnung', path: '/invoices/add', icon: FileText, permission: 'create:invoices' },
      { name: 'Neue Zahlung', path: '/payments/add', icon: CreditCard, permission: 'create:payments' }
    ]
  }
];

const profileMenuSections = [
  {
    title: 'Profil & Account',
    items: [
      { name: 'Profil bearbeiten', path: '/profile', icon: User, permission: 'edit:profile' },
      { name: 'Account Einstellungen', path: '/account', icon: Settings, permission: 'manage:account' },
      { name: 'Sicherheit', path: '/security', icon: Shield, permission: 'manage:security' }
    ]
  },
  {
    title: 'Support & Hilfe',
    items: [
      { name: 'Dokumentation', path: '/docs', icon: FileText, permission: 'read:docs' },
      { name: 'Support kontaktieren', path: '/support', icon: Mail, permission: 'contact:support' },
      { name: 'Hilfe & FAQs', path: '/help', icon: HelpCircle, permission: 'read:help' }
    ]
  }
];

/* ----------------------------- Breadcrumb Utils ---------------------------- */
const isIdSegment = (seg) => {
  // rein numerisch oder UUID
  return /^\d+$/.test(seg) || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(seg);
};

const shortId = (seg) => {
  if (/^\d+$/.test(seg)) return seg; // Zahlen so lassen
  // UUID kurze Form: letzte 6 Zeichen ohne Bindestriche
  return seg.replace(/-/g, '').toUpperCase().slice(-6);
};

const humanize = (seg) =>
  seg
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

const labelFor = (seg, prevLabel) => {
  // 1) direkte Map
  if (pathMap[seg]) return pathMap[seg];
  // 2) zusammengesetzte Routen wie "applicants/add"
  if (prevLabel && pathMap[`${seg}-add`]) return pathMap[`${seg}-add`];
  // 3) fallback humanize
  return humanize(seg);
};

const buildBreadcrumbs = (pathname) => {
  const raw = pathname.split('/').filter(Boolean);
  const crumbs = [];

  raw.forEach((seg, idx) => {
    const to = '/' + raw.slice(0, idx + 1).join('/');

    if (isIdSegment(seg)) {
      // ID hängt an den vorherigen Titel: "Bewerber #A1B2C3"
      if (crumbs.length) {
        const last = { ...crumbs.pop() };
        last.name = `${last.name} #${shortId(seg)}`;
        last.to = to;
        crumbs.push(last);
      } else {
        // Edge-Case: ID ohne vorherigen Title
        crumbs.push({ name: `#${shortId(seg)}`, to });
      }
    } else {
      const prevLabel = crumbs.length ? crumbs[crumbs.length - 1].name : undefined;
      crumbs.push({ name: labelFor(seg, prevLabel), to });
    }
  });

  // Home immer vorne
  return [
    { name: 'Home', to: '/dashboard', isHome: true },
    ...crumbs
  ];
};

/* --------------------------------- TopNav --------------------------------- */
const TopNav = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [userPermissions, setUserPermissions] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);
  const quickActionsRef = useRef(null);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        const permissions = decodedToken.permissions || [];
        setUserPermissions(permissions);
      } catch (error) {
        console.error('Error getting permissions:', error);
      }
    };
    checkPermissions();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown === 'search' && searchRef.current && !searchRef.current.contains(event.target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'notifications' && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'userMenu' && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      } else if (activeDropdown === 'quickActions' && quickActionsRef.current && !quickActionsRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && activeDropdown) setActiveDropdown(null);
    };
    const handleRouteChange = () => setActiveDropdown(null);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [activeDropdown]);

  const hasPermission = (p) => (!p ? true : userPermissions.includes(p));
  const hasAccessToSection = (section) => section.items.some(i => hasPermission(i.permission));
  const hasAnyQuickActions = () => quickActions.some(hasAccessToSection);

  const breadcrumbs = React.useMemo(
    () => buildBreadcrumbs(location.pathname),
    [location.pathname]
  );

  const performSearch = (query) => {
    setIsSearching(true);
    setTimeout(() => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      const demoData = [
        { id: 1, firstName: 'Max', lastName: 'Mustermann', email: 'max.mustermann@example.com', type: 'applicant' },
        { id: 2, firstName: 'Anna', lastName: 'Schmidt', email: 'anna.schmidt@example.com', type: 'applicant' },
        { id: 3, firstName: 'Thomas', lastName: 'Weber', email: 'thomas.weber@example.com', type: 'employee' },
        { id: 4, firstName: 'Laura', lastName: 'Meyer', email: 'laura.meyer@example.com', type: 'partner' },
        { id: 5, firstName: 'Michael', lastName: 'Becker', email: 'michael.becker@example.com', type: 'customer' },
        { id: 6, firstName: 'Restaurant', lastName: 'Adler', email: 'info@adler-restaurant.com', type: 'location' },
      ];
      const lower = query.toLowerCase();
      const results = demoData.filter(i =>
        i.firstName.toLowerCase().includes(lower) ||
        i.lastName.toLowerCase().includes(lower) ||
        i.email.toLowerCase().includes(lower)
      );
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    performSearch(q);
    if (q.trim()) {
      setRecentSearches(prev => {
        const updated = prev.filter(item => item !== q);
        return [q, ...updated].slice(0, 5);
      });
    }
  };

  const handleSelectSearchResult = (result) => {
    setActiveDropdown(null);
    switch (result.type) {
      case 'applicant': navigate(`/applicants/${result.id}`); break;
      case 'employee': navigate(`/employees/${result.id}`); break;
      case 'partner': navigate(`/partners/${result.id}`); break;
      case 'customer': navigate(`/customers/${result.id}`); break;
      case 'location': navigate(`/locations/${result.id}`); break;
      default: navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const selectRecentSearch = (q) => {
    setSearchQuery(q);
    performSearch(q);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const toggleDropdown = (name) => {
    const isOpening = activeDropdown !== name;
    setActiveDropdown(activeDropdown === name ? null : name);
    if (name === 'search' && isOpening) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
        <div className="flex justify-between h-16">
          {/* Left: Breadcrumbs (clean, industry-standard) */}
          <div className="flex items-center overflow-x-auto">
            <nav aria-label="Breadcrumb" className="w-full">
              <ol className="flex items-center text-sm text-gray-600">
                {breadcrumbs.map((bc, idx) => {
                  const isLast = idx === breadcrumbs.length - 1;
                  return (
                    <li key={bc.to} className="flex items-center">
                      {idx > 0 && (
                        <ChevronRight aria-hidden="true" className="mx-2 h-4 w-4 text-gray-300" />
                      )}
                      {!isLast ? (
                        <Link
                          to={bc.to}
                          className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors max-w-[220px]"
                          title={bc.name}
                        >
                          {bc.isHome && <Home className="h-4 w-4 shrink-0" />}
                          <span className="truncate">{bc.name}</span>
                        </Link>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-gray-900 font-medium max-w-[260px]"
                          aria-current="page"
                          title={bc.name}
                        >
                          {bc.isHome && <Home className="h-4 w-4 shrink-0" />}
                          <span className="truncate">{bc.name}</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-1">
            {/* Quick Actions */}
            {hasAnyQuickActions() && (
              <div className="relative" ref={quickActionsRef}>
                <button 
                  className={`p-2 rounded-md transition-colors ${
                    activeDropdown === 'quickActions' 
                      ? 'text-gray-900 bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDropdown('quickActions')}
                  aria-expanded={activeDropdown === 'quickActions'}
                  aria-haspopup="true"
                >
                  <Plus className="w-5 h-5" />
                </button>
                
                {activeDropdown === 'quickActions' && (
                  <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="grid grid-cols-1 divide-y divide-gray-100">
                      {quickActions.map((section, idx) => (
                        hasAccessToSection(section) && (
                          <div key={idx} className="p-4">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                              {section.title}
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                              {section.items.map((item, itemIdx) => (
                                hasPermission(item.permission) && (
                                  <button
                                    key={itemIdx}
                                    onClick={() => {
                                      navigate(item.path);
                                      setActiveDropdown(null);
                                    }}
                                    className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
                                  >
                                    <div className="flex-shrink-0 w-8 h-8 bg-blue-50 text-blue-600 rounded-md flex items-center justify-center">
                                      <item.icon className="w-4 h-4" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">
                                      {item.name}
                                    </span>
                                  </button>
                                )
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Search */}
            {hasPermission('read:search') && (
              <div className="relative" ref={searchRef}>
                <button 
                  className={`p-2 rounded-md transition-colors ${
                    activeDropdown === 'search' 
                      ? 'text-gray-900 bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDropdown('search')}
                  aria-expanded={activeDropdown === 'search'}
                  aria-haspopup="true"
                >
                  <Search className="w-5 h-5" />
                </button>
                
                {activeDropdown === 'search' && (
                  <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="p-4">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          placeholder="Suche nach Namen, E-Mails, IDs..."
                          className="block w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchQuery && (
                          <button
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => handleSearch('')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {!searchQuery && recentSearches.length > 0 && (
                      <div className="px-4 pb-3">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Letzte Suchen</h3>
                        <div className="flex flex-wrap gap-2">
                          {recentSearches.map((q, idx) => (
                            <button
                              key={idx}
                              onClick={() => selectRecentSearch(q)}
                              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors"
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {isSearching && (
                      <div className="border-t border-gray-100 px-4 py-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                        <p className="text-sm text-gray-500 mt-2">Suche läuft...</p>
                      </div>
                    )}
                    {!isSearching && searchQuery && (
                      <div className="border-t border-gray-100 max-h-[400px] overflow-y-auto">
                        {searchResults.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className="text-sm text-gray-500">Keine Ergebnisse für "{searchQuery}"</p>
                          </div>
                        ) : (
                          <>
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                              <p className="text-xs text-gray-500">{searchResults.length} Ergebnisse gefunden</p>
                            </div>
                            {searchResults.map((result) => (
                              <div 
                                key={`${result.type}-${result.id}`}
                                className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
                                onClick={() => handleSelectSearchResult(result)}
                              >
                                <div className="flex items-center">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {result.firstName} {result.lastName}
                                    </div>
                                    <div className="text-sm text-gray-500 mt-0.5">{result.email}</div>
                                  </div>
                                  <div className="ml-4">
                                    <span className={`
                                      inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                      ${result.type === 'applicant' ? 'bg-blue-100 text-blue-800' : ''}
                                      ${result.type === 'employee' ? 'bg-green-100 text-green-800' : ''}
                                      ${result.type === 'partner' ? 'bg-purple-100 text-purple-800' : ''}
                                      ${result.type === 'customer' ? 'bg-yellow-100 text-yellow-800' : ''}
                                      ${result.type === 'location' ? 'bg-gray-100 text-gray-800' : ''}
                                    `}>
                                      {result.type === 'applicant' && 'Bewerber'}
                                      {result.type === 'employee' && 'Mitarbeiter'}
                                      {result.type === 'partner' && 'Partner'}
                                      {result.type === 'customer' && 'Kunde'}
                                      {result.type === 'location' && 'Location'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      <button 
                        onClick={() => {
                          navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                          setActiveDropdown(null);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 w-full text-center"
                      >
                        Erweiterte Suche
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notifications */}
            {hasPermission('read:notifications') && (
              <div className="relative" ref={notificationsRef}>
                <button 
                  className={`p-2 rounded-md transition-colors ${
                    activeDropdown === 'notifications' 
                      ? 'text-gray-900 bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => toggleDropdown('notifications')}
                  aria-expanded={activeDropdown === 'notifications'}
                  aria-haspopup="true"
                >
                  <div className="relative">
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 rounded-full text-[10px] font-medium text-white flex items-center justify-center">
                      3
                    </span>
                  </div>
                </button>

                {activeDropdown === 'notifications' && (
                  <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Benachrichtigungen</h3>
                        <button className="text-sm text-blue-600 hover:text-blue-800">
                          Alle als gelesen markieren
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
                      {[
                        { title: 'Neue Bewerbung eingegangen', description: 'Max Mustermann hat sich als Koch beworben', time: 'Vor 5 Min.', unread: true },
                        { title: 'Abrechnungserinnerung', description: 'Die Monatsabrechnung für Mai ist fällig', time: 'Vor 1 Std.', unread: true },
                        { title: 'Systemupdate verfügbar', description: 'Eine neue Version ist verfügbar', time: 'Vor 1 Tag', unread: false }
                      ].map((n, idx) => (
                        <div key={idx} className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${n.unread ? 'bg-blue-50/50' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{n.title}</div>
                              <div className="text-sm text-gray-500 mt-0.5">{n.description}</div>
                              <div className="text-xs text-gray-400 mt-1">{n.time}</div>
                            </div>
                            {n.unread && <div className="w-2 h-2 bg-blue-600 rounded-full mt-1.5" />}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-100 bg-gray-50">
                      <button 
                        onClick={() => {
                          navigate('/notifications');
                          setActiveDropdown(null);
                        }}
                        className="text-sm text-gray-700 hover:text-gray-900 w-full text-center"
                      >
                        Alle Benachrichtigungen anzeigen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Menu */}
            <div className="relative ml-2" ref={userMenuRef}>
              <button
                onClick={() => toggleDropdown('userMenu')}
                className={`flex items-center space-x-3 px-2 py-1.5 rounded-md transition-colors ${activeDropdown === 'userMenu' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                aria-expanded={activeDropdown === 'userMenu'}
                aria-haspopup="true"
              >
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user?.name || "User"}
                    className="w-8 h-8 rounded-full ring-2 ring-white"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "https://ui-avatars.com/api/?name=User&size=200";
                    }}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
                <div className="hidden sm:flex sm:items-center sm:space-x-2">
                  <span className="text-sm font-medium text-gray-900">{user?.name || "Benutzer"}</span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </div>
              </button>

              {activeDropdown === 'userMenu' && (
                <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="p-4 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      {user?.picture ? (
                        <img
                          src={user.picture}
                          alt={user?.name || "User"}
                          className="w-12 h-12 rounded-full ring-2 ring-white"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "https://ui-avatars.com/api/?name=User&size=200";
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{user?.name || "Benutzer"}</div>
                        <div className="text-sm text-gray-500">{user?.email || ""}</div>
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    {profileMenuSections.map((section, sectionIdx) => {
                      const hasAccess = section.items.some(item => hasPermission(item.permission));
                      return hasAccess && (
                        <div key={sectionIdx} className="px-2">
                          <div className="px-3 py-2">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              {section.title}
                            </h3>
                          </div>
                          {section.items.map((item, itemIdx) => (
                            hasPermission(item.permission) && (
                              <button
                                key={itemIdx}
                                onClick={() => {
                                  navigate(item.path);
                                  setActiveDropdown(null);
                                }}
                                className="flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
                              >
                                <div className="flex-shrink-0 w-8 h-8 bg-gray-50 text-gray-600 rounded-md flex items-center justify-center">
                                  <item.icon className="w-4 h-4" />
                                </div>
                                <span className="text-sm text-gray-700">{item.name}</span>
                              </button>
                            )
                          ))}
                          {sectionIdx < profileMenuSections.length - 1 && hasAccess && (
                            <div className="my-2 border-t border-gray-100" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="px-2 py-2 border-t border-gray-100">
                    <button
                      onClick={() => logout({ returnTo: window.location.origin })}
                      className="flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-red-50 transition-colors w-full text-left text-red-600"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-md flex items-center justify-center">
                        <LogOut className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">Abmelden</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
